import {ThresholdLimit} from '@domain/kiln-monitoring'
import CloseIcon from '@mui/icons-material/Close'
import ErrorIcon from '@mui/icons-material/Error'
import WarningIcon from '@mui/icons-material/Warning'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import {Box, Chip, ListSubheader, Menu, Checkbox, ListItem, Typography} from '@mui/material'
import {MouseEventHandler, useState} from 'react'
import {useTranslation} from 'react-i18next'

import {SignalSourceIcon} from '../../../shared/components/SignalSourceIcon'
import {
  FilterOption,
  statusOptions,
  StatusOption,
  filterOptions
} from '../../../shared/types/plantMonitoring.types'
import {signalProviders} from '../../../shared/types/sensor.types'
import {usePlantMonitoring} from '../../context/PlantMonitoringContext'

export const SignalTreeFilters = () => {
  const {t} = useTranslation()
  const [openFilter, setOpenFilter] = useState<FilterOption | null>(null)
  const {state, actions} = usePlantMonitoring()
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)

  const {providers, status} = state.filters

  const open = Boolean(anchorEl)
  const noFiltersSelected = providers.length === 0 && status.length === 0

  const handleClick = (filter: FilterOption) => (event: React.MouseEvent<HTMLDivElement>) => {
    setOpenFilter(filter)
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
    setOpenFilter(null)
  }

  return (
    <>
      <Box
        gap={1}
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          flexDirection: {xs: 'column', sm: 'row'}
        }}
      >
        <Chip
          color={noFiltersSelected ? 'primary' : 'default'}
          sx={{
            fontSize: 16,
            color: noFiltersSelected ? 'default' : '#52667A',
            fontWeight: 500
          }}
          label={t('signalTree.all')}
          onClick={noFiltersSelected ? undefined : actions.clearAllFilters}
        />
        {filterOptions.map((option) => (
          <FilterChip
            key={option}
            open={openFilter === option}
            filterOption={option}
            onClick={handleClick(option)}
            onDelete={() => actions.clearFilter(option)}
          />
        ))}
      </Box>
      <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
        {openFilter === 'Status' && <StatusFilterOptions />}
        {openFilter === 'Provider' && <ProviderFilterOptions />}
      </Menu>
    </>
  )
}

const FilterChip = ({
  open,
  filterOption,
  onClick,
  onDelete
}: {
  open: boolean
  filterOption: FilterOption
  onClick: MouseEventHandler<HTMLDivElement>
  onDelete: () => void
}) => {
  const {t} = useTranslation()
  const selectedValues = useFilterValues({filterOption})
  const active = selectedValues.length > 0 || open
  const multipleSelected = selectedValues.length > 1
  const oneSelected = selectedValues.length === 1
  const atLeastOneSelected = selectedValues.length > 0

  return (
    <Chip
      key={filterOption}
      color={active ? 'primary' : 'default'}
      label={
        <Box display="flex" gap={0.5}>
          <Typography variant="h5" color={active ? 'rgba(255, 255, 255, 0.60)' : '#52667A'}>
            {/* ! DYNAMIC TRANSLATION KEY ! */}
            {t(`signalTree.${filterOption.toLowerCase()}Filter`)}
          </Typography>
          <Typography variant="h5">
            {oneSelected
              ? selectedValues[0]
              : multipleSelected
                ? `${t('signalTree.multiple')} (${selectedValues.length})`
                : null}
          </Typography>
        </Box>
      }
      onClick={onClick}
      onDelete={atLeastOneSelected ? onDelete : undefined}
      deleteIcon={<CloseIcon sx={{fontSize: '16px !important'}} />}
    />
  )
}

const useFilterValues = ({filterOption}: {filterOption: FilterOption}) => {
  const {state} = usePlantMonitoring()
  const {providers, status} = state.filters

  if (filterOption === 'Provider') {
    return providers
  }
  if (filterOption === 'Status') {
    return status
  }
  return []
}

const ProviderFilterOptions = () => {
  const {t} = useTranslation()
  const {state, actions} = usePlantMonitoring()
  const {providers} = state.filters

  return (
    <>
      <ListSubheader sx={{textAlign: 'center'}}>{t('signalTree.providerFilter')}</ListSubheader>
      {signalProviders.map((option) => (
        <ListItem key={option} divider>
          <Checkbox
            size="small"
            checked={providers.includes(option)}
            onChange={() => actions.toggleProvider(option)}
          />
          <Box display="flex" gap={1} alignItems="center">
            <SignalSourceIcon source={option} />
            {option}
          </Box>
        </ListItem>
      ))}
    </>
  )
}

const StatusFilterOptions = () => {
  const {state, actions} = usePlantMonitoring()
  const {status} = state.filters

  return (
    <>
      {statusOptions.map((option) => (
        <ListItem key={option} divider>
          <Checkbox
            size="small"
            checked={status.includes(option)}
            onChange={() => actions.toggleStatus(option)}
          />
          <Box display="flex" gap={1} alignItems="center">
            <StatusIcon status={option} />
            {option}
          </Box>
        </ListItem>
      ))}
    </>
  )
}

const StatusIcon = ({status}: {status: StatusOption}) => {
  switch (status) {
    case ThresholdLimit.HIGH_HIGH:
    case ThresholdLimit.LOW_LOW:
      return <ErrorIcon color="error" />
    case ThresholdLimit.HIGH:
    case ThresholdLimit.LOW:
      return <WarningAmberIcon color="warning" />
    case 'ModelBreach':
      return <WarningIcon style={{color: '#8C3CC9'}} />
    default:
      return null
  }
}
